// mapFragmentInit.js — Y.XmlFragment helpers for the report's single shared map.
//
// Ticket 01 (shared-map-data): every map node embed in a report is a "window" onto ONE shared
// map — a single Y.XmlFragment (fixed key) holding the report's layers/markings/saved views (and,
// per ticket 02, the shared DTG range). There is no per-nodeId fragment anymore; a map node's own
// PM attrs (see prosemirror-editor/core/nodes/mapNode.js) hold only that embed's own
// window-only state (which view/DTG scrub point/basemap/etc. it's currently showing).

import {prosemirrorToYXmlFragment} from 'y-prosemirror';
import {createEmptyMapDocJSON, mapSchema} from './schema/mapSchema';

const SHARED_FRAGMENT_KEY = 'shared-map';

/**
 * The report's single shared map fragment. Every map node embed reads/writes this same
 * fragment — there is no per-nodeId variant.
 *
 * @param {import('yjs').Doc} ydoc
 * @returns {import('yjs').XmlFragment|null}
 */
export function getSharedMapFragment(ydoc) {
    if (!ydoc) return null;
    return ydoc.getXmlFragment(SHARED_FRAGMENT_KEY);
}

/**
 * Ensure the report's shared Y.XmlFragment exists with an empty map PM doc. No-op when the
 * fragment already has content — i.e. some earlier map node in this report (or a collaborator)
 * already initialized it, in which case every embed should see that same existing content
 * rather than each insert clobbering it back to empty.
 *
 * @param {import('yjs').Doc} ydoc
 * @returns {import('yjs').XmlFragment|null}
 */
export function initializeSharedMapFragment(ydoc) {
    const fragment = getSharedMapFragment(ydoc);
    if (!fragment) return null;
    if (fragment.length > 0) return fragment;

    const doc = mapSchema.nodeFromJSON(createEmptyMapDocJSON());
    ydoc.transact(() => {
        prosemirrorToYXmlFragment(doc, fragment);
    }, 'map-init');
    return fragment;
}
